/**
 * Notifications Drawer Component
 * Desktop: Right-side drawer
 * Mobile: Bottom sheet
 */

import React, { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useNavigate } from 'react-router-dom';
import BottomSheet from './BottomSheet.jsx';

export default function NotificationsDrawer({ 
  isOpen, 
  onClose, 
  attentionItems = [], 
  onItemClick 
}) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(false);

  useEffect(() => {
    const checkMobile = () => window.innerWidth < 768;
    setIsMobile(checkMobile());
    window.addEventListener('resize', () => setIsMobile(checkMobile()));
    return () => window.removeEventListener('resize', () => setIsMobile(checkMobile()));
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleViewAll = () => {
    onClose();
    navigate('/owner/notifications');
  };

  const handleItemClick = (item) => {
    onClose();
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const getItemLabel = (item) => {
    switch (item.type) {
      case 'check-in-24h':
        return `Check-in within 24h: ${item.booking?.guest_name || 'Guest'}`;
      case 'check-in-48h':
        return `Check-in within 48h: ${item.booking?.guest_name || 'Guest'}`;
      case 'payment-pending':
        return `Payment pending: ${item.booking?.guest_name || 'Guest'}`;
      case 'taxes-due':
        return `Taxes due: ${item.amount ? `$${item.amount.toLocaleString()}` : ''}`;
      case 'gap':
        return `Gap opportunity: ${item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}`;
      default:
        return item.label || 'Attention needed';
    }
  };

  const getItemUrgency = (item) => {
    if (item.type === 'check-in-24h') return 'urgent';
    if (item.type === 'check-in-48h') return 'high';
    return 'medium';
  };

  const DrawerContent = (
    <div style={{
      ...styles.drawer,
      backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
    }}>
      <div style={styles.header}>
        <h2 style={{
          ...styles.title,
          color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
        }}>
          Notifications
        </h2>
        <button
          onClick={onClose}
          style={styles.closeButton}
          aria-label="Close notifications"
        >
          ×
        </button>
      </div>

      <div style={styles.content}>
        {attentionItems.length > 0 ? (
          <>
            <div style={styles.itemsList}>
              {attentionItems.map((item, index) => {
                const urgency = getItemUrgency(item);
                return (
                  <button
                    key={index}
                    onClick={() => handleItemClick(item)}
                    style={{
                      ...styles.item,
                      borderLeftColor: urgency === 'urgent'
                        ? 'var(--color-status-error-base)'
                        : urgency === 'high'
                        ? 'var(--color-status-warning-base)'
                        : 'var(--color-status-info-base)',
                      backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
                      color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                    }}
                    aria-label={getItemLabel(item)}
                  >
                    <div style={styles.itemContent}>
                      <div style={{
                        ...styles.itemLabel,
                        color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                      }}>
                        {getItemLabel(item)}
                      </div>
                      {item.description && (
                        <div style={{
                          ...styles.itemDescription,
                          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                        }}>
                          {item.description}
                        </div>
                      )}
                    </div>
                    <span style={styles.itemAction}>→</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={handleViewAll}
              style={{
                ...styles.viewAllButton,
                color: theme === 'dark' ? 'var(--color-primary-light-dark)' : 'var(--color-primary-base)',
              }}
            >
              View all notifications →
            </button>
          </>
        ) : (
          <div style={{
            ...styles.emptyState,
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          }}>
            No notifications at this time
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet isOpen={isOpen} onClose={onClose}>
        {DrawerContent}
      </BottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <>
      <div style={styles.backdrop} onClick={onClose} />
      <div style={styles.drawerContainer}>
        {DrawerContent}
      </div>
    </>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 'calc(var(--z-modal) - 1)',
  },
  drawerContainer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '400px',
    maxWidth: '90vw',
    zIndex: 'var(--z-modal)',
    boxShadow: 'var(--shadow-2xl)',
  },
  drawer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-bg-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-5)',
    borderBottom: '1px solid var(--color-border-light)',
  },
  title: {
    fontSize: 'var(--typography-level-2-size)',
    fontWeight: 'var(--typography-level-2-weight)',
    color: 'var(--typography-level-2-color)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-2xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 'var(--spacing-4)',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    borderLeft: '4px solid',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    textAlign: 'left',
    width: '100%',
    border: 'none',
    color: 'var(--color-text-primary)',
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-2)',
    flex: 1,
  },
  itemLabel: {
    fontSize: 'var(--typography-level-4-size)',
    fontWeight: 'var(--typography-level-4-weight)',
    color: 'var(--typography-level-4-color)',
    lineHeight: 'var(--typography-level-4-line-height)',
  },
  itemDescription: {
    fontSize: 'var(--typography-level-5-size)',
    fontWeight: 'var(--typography-level-5-weight)',
    color: 'var(--typography-level-5-color)',
    lineHeight: 'var(--typography-level-5-line-height)',
  },
  itemAction: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-text-secondary)',
    marginLeft: 'var(--spacing-2)',
  },
  viewAllButton: {
    padding: 'var(--spacing-4)',
    borderTop: '1px solid var(--color-border-light)',
    background: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-primary-base)',
    cursor: 'pointer',
    transition: 'var(--transition-base)',
    textAlign: 'center',
    width: '100%',
    marginTop: 'var(--spacing-4)',
  },
  emptyState: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
};

